import React, { useMemo } from 'react';
import {
  Drawer,
  Box,
  Stack,
  Typography,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Button,
  Divider,
  Alert,
  Chip,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { ComparisonGroupDefinition } from '../../types/schema';
import YearPicker from '../common/YearPicker';

interface GroupComparisonBuilderProps {
  open: boolean;
  groups: ComparisonGroupDefinition[];
  availableTags: string[];
  availableCountries: string[];
  historyAttributeValues: Record<string, string[]>;
  startYear: number;
  endYear: number;
  availableYears?: number[];
  onStartYearChange: (year: number) => void;
  onEndYearChange: (year: number) => void;
  isSubmitting: boolean;
  error?: string | null;
  hasResult: boolean;
  onGroupsChange: (groups: ComparisonGroupDefinition[]) => void;
  onSubmit: () => void;
  onClose: () => void;
  onClearResults: () => void;
}

const ATTRIBUTE_OPTIONS = [
  { value: 'tags', label: 'Tags' },
  { value: 'land_cover', label: 'Land cover' },
  { value: 'crop', label: 'Crop' },
  { value: 'primary_practice', label: 'Primary practice' },
  { value: 'secondary_practice', label: 'Secondary practice' },
  { value: 'data_type', label: 'Data type' },
  { value: 'sourcing_year', label: 'Sourcing year' },
  { value: 'country', label: 'Country' },
] as const;

type AttributeType = typeof ATTRIBUTE_OPTIONS[number]['value'];

const ensureTwoGroups = (groups: ComparisonGroupDefinition[]): ComparisonGroupDefinition[] => {
  if (groups.length >= 2) {
    return groups;
  }

  const defaults: ComparisonGroupDefinition[] = [
    { label: 'Group 1', filters: {} },
    { label: 'Group 2', filters: {} },
  ];

  return defaults.map((fallback, index) => groups[index] || fallback);
};

export const GroupComparisonBuilder: React.FC<GroupComparisonBuilderProps> = ({
  open,
  groups,
  availableTags,
  availableCountries,
  historyAttributeValues,
  startYear,
  endYear,
  availableYears,
  onStartYearChange,
  onEndYearChange,
  isSubmitting,
  error,
  hasResult,
  onGroupsChange,
  onSubmit,
  onClose,
  onClearResults,
}) => {
  const normalizedGroups = useMemo(() => ensureTwoGroups(groups), [groups]);

  const handleLabelChange = (index: number, value: string) => {
    const updated = normalizedGroups.map((group, i) =>
      i === index ? { ...group, label: value } : group
    );
    onGroupsChange(updated);
  };

  const handleAddFilter = (groupIndex: number, attributeType: AttributeType) => {
    const updated = normalizedGroups.map((group, i) => {
      if (i !== groupIndex) return group;
      const newFilters = { ...group.filters };
      if (attributeType === 'tags') {
        newFilters.tags = newFilters.tags || [];
      } else {
        newFilters.historyFilters = { ...newFilters.historyFilters };
        newFilters.historyFilters[attributeType] = [];
      }
      return { ...group, filters: newFilters };
    });
    onGroupsChange(updated);
  };

  const handleRemoveFilter = (groupIndex: number, attributeType: AttributeType) => {
    const updated = normalizedGroups.map((group, i) => {
      if (i !== groupIndex) return group;
      const newFilters = { ...group.filters };
      if (attributeType === 'tags') {
        delete newFilters.tags;
      } else {
        const newHistoryFilters = { ...newFilters.historyFilters };
        delete newHistoryFilters[attributeType];
        newFilters.historyFilters = Object.keys(newHistoryFilters).length > 0 ? newHistoryFilters : undefined;
      }
      return { ...group, filters: newFilters };
    });
    onGroupsChange(updated);
  };

  const handleTagsChange = (groupIndex: number, tags: string[]) => {
    const updated = normalizedGroups.map((group, i) =>
      i === groupIndex
        ? { ...group, filters: { ...group.filters, tags: tags.length > 0 ? tags : undefined } }
        : group
    );
    onGroupsChange(updated);
  };

  const handleHistoryFilterChange = (groupIndex: number, attributeType: string, values: string[]) => {
    const updated = normalizedGroups.map((group, i) => {
      if (i !== groupIndex) return group;
      const newFilters = { ...group.filters };
      newFilters.historyFilters = { ...newFilters.historyFilters };
      if (values.length > 0) {
        newFilters.historyFilters[attributeType] = values;
      } else {
        delete newFilters.historyFilters[attributeType];
      }
      if (Object.keys(newFilters.historyFilters).length === 0) {
        newFilters.historyFilters = undefined;
      }
      return { ...group, filters: newFilters };
    });
    onGroupsChange(updated);
  };

  const getAvailableAttributes = (groupIndex: number): AttributeType[] => {
    const group = normalizedGroups[groupIndex];
    const usedAttributes = new Set<AttributeType>();
    if (group.filters.tags) usedAttributes.add('tags');
    if (group.filters.historyFilters) {
      Object.keys(group.filters.historyFilters).forEach((key) => {
        usedAttributes.add(key as AttributeType);
      });
    }
    return ATTRIBUTE_OPTIONS.map((opt) => opt.value).filter((attr) => !usedAttributes.has(attr));
  };

  const getOptionsForAttribute = (attributeType: AttributeType): string[] => {
    if (attributeType === 'tags') {
      return availableTags;
    }
    if (attributeType === 'country') {
      return availableCountries;
    }
    // data_type is a static attribute with fixed allowed values
    if (attributeType === 'data_type') {
      return ['Primary', 'Global Average'];
    }
    return historyAttributeValues[attributeType] || [];
  };

  const canSubmit = normalizedGroups.every((group) => {
    const hasTags = group.filters.tags && group.filters.tags.length > 0;
    const hasHistoryFilters =
      group.filters.historyFilters &&
      Object.values(group.filters.historyFilters).some((vals) => vals && vals.length > 0);
    return hasTags || hasHistoryFilters;
  });

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}
    >
      <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Compare Groups
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>

        <Typography variant="body2" color="text.secondary">
          Define two ad-hoc groups to compare their NEE performance.
        </Typography>

        <Stack spacing={2} direction="row" sx={{ flexWrap: 'wrap' }}>
          <YearPicker
            label="Start Year"
            value={new Date(startYear, 0, 1)}
            onChange={(date) => date && onStartYearChange(date.getFullYear())}
            minDate={new Date(2000, 0, 1)}
            maxDate={new Date(endYear, 11, 31)}
            availableYears={availableYears}
          />
          <YearPicker
            label="End Year"
            value={new Date(endYear, 0, 1)}
            onChange={(date) => date && onEndYearChange(date.getFullYear())}
            minDate={new Date(startYear, 0, 1)}
            maxDate={new Date()}
            availableYears={availableYears}
          />
        </Stack>

        <Stack spacing={3} sx={{ flexGrow: 1, overflowY: 'auto', pr: 1 }}>
          {normalizedGroups.map((group, index) => {
            const availableAttributes = getAvailableAttributes(index);
            const activeFilters: Array<{ type: AttributeType; label: string }> = [];
            if (group.filters.tags) {
              activeFilters.push({ type: 'tags', label: 'Tags' });
            }
            if (group.filters.historyFilters) {
              Object.keys(group.filters.historyFilters).forEach((key) => {
                const option = ATTRIBUTE_OPTIONS.find((opt) => opt.value === key);
                if (option) {
                  activeFilters.push({ type: key as AttributeType, label: option.label });
                }
              });
            }

            return (
              <Box key={index}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {`Group ${index + 1}`}
                  </Typography>
                </Stack>
                <Stack spacing={2}>
                  <TextField
                    label="Label"
                    value={group.label}
                    onChange={(event) => handleLabelChange(index, event.target.value)}
                    fullWidth
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Add filters to define this group. Polygons must match ALL filters (AND logic).
                  </Typography>
                  {activeFilters.map((filter) => {
                    const options = getOptionsForAttribute(filter.type);
                    const currentValues =
                      filter.type === 'tags'
                        ? group.filters.tags || []
                        : group.filters.historyFilters?.[filter.type] || [];

                    return (
                      <Paper key={filter.type} elevation={1} sx={{ p: 2 }}>
                        <Stack spacing={2}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {filter.label}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveFilter(index, filter.type)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                          <Autocomplete
                            multiple
                            freeSolo
                            options={options}
                            value={currentValues}
                            onChange={(_, newValue) => {
                              if (filter.type === 'tags') {
                                handleTagsChange(index, newValue);
                              } else {
                                handleHistoryFilterChange(index, filter.type, newValue);
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={`Select ${filter.label.toLowerCase()}`}
                                placeholder="Select or enter values"
                                size="small"
                              />
                            )}
                            renderTags={(value, getTagProps) =>
                              value.map((option, tagIndex) => (
                                <Chip
                                  {...getTagProps({ index: tagIndex })}
                                  key={option}
                                  label={option}
                                  size="small"
                                />
                              ))
                            }
                          />
                        </Stack>
                      </Paper>
                    );
                  })}
                  {availableAttributes.length > 0 && (
                    <FormControl fullWidth>
                      <InputLabel>Add filter</InputLabel>
                      <Select
                        value=""
                        label="Add filter"
                        onChange={(event) => {
                          const attrType = event.target.value as AttributeType;
                          if (attrType) {
                            handleAddFilter(index, attrType);
                          }
                        }}
                      >
                        {availableAttributes.map((attrValue) => {
                          const option = ATTRIBUTE_OPTIONS.find((opt) => opt.value === attrValue);
                          return (
                            <MenuItem key={attrValue} value={attrValue}>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <AddIcon fontSize="small" />
                                <span>Add {option?.label || attrValue}</span>
                              </Stack>
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  )}
                </Stack>
                {index === 0 && <Divider sx={{ mt: 3 }} />}
              </Box>
            );
          })}
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="text" onClick={onClearResults} disabled={!hasResult || isSubmitting}>
            Clear results
          </Button>
          <Button
            variant="contained"
            onClick={onSubmit}
            disabled={!canSubmit || isSubmitting}
          >
            {isSubmitting ? 'Comparing...' : 'Run comparison'}
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default GroupComparisonBuilder;
